import React, { useState } from 'react'
import {
  Card,
  FilterButton,
  Button,
  IconButton,
  Toaster,
  FilterContainer,
  Flex,
  prettyNumber,
  MultiLocationSelect,
  MultiHRLocationSelect,
  BuildingTypeSelect,
  Popover,
  Icon,
  LinkButton,
  Dialog,
} from '@ui'
import { Collapse, Classes, H6, Switch, Menu, MenuItem, Position } from '@blueprintjs/core'
import { NavigateFn } from '@reach/router'
import { NewBuildingForm } from './BuildingForm'
import { pluralize } from '@utils'
import { Mode } from '@ui/selects/LocationSelect'
import { useReload } from '@hooks/reloadContext'
import { UseBuildingsHook } from './hooks/useBuildings'
import track, { RootEvent } from '@track'
import Event from '@track/Event'
import Permissions from '@permissions/Permissions'
import { PermissionsType, IPermissionsOnBuilding } from '@permissions/types'
import ImportFlow from 'components/ImportFlow'
import api from '@api'
import useImport, { Import } from 'components/ImportFlow/hooks/useImport'
import css from './toolbar.module.scss'

const dlTemplate = (fileName: string) => (blob: string) => {
  const a = document.createElement('a')
  a.download = fileName
  a.target = '_blank'
  a.href = window.URL.createObjectURL(new Blob([blob], { type: 'text/plain' }))
  a.click()
}

export default function Toolbar({
  buildings,
  navigate,
  showFilters,
  setShowFilters,
}: {
  buildings: UseBuildingsHook
  navigate?: NavigateFn
  toggleListView: () => void
  listView: boolean
  showFilters: boolean
  setShowFilters: (showFilters: boolean) => void
}) {
  const [showNew, setShowNew] = useState(false)
  const reload = useReload()

  const importBuildings = useImport(Import.BUILDING)
  const importFloors = useImport(Import.FLOOR)

  const [importingFloors, setImportingFloors] = useState(false)
  const [importingBuildings, setImportingBuildings] = useState(false)

  let filterCount = 0

  if (buildings.filters.includeOld) {
    filterCount++
  }

  if (buildings.filters.cities.length) {
    filterCount++
  }

  if (buildings.filters.HRLocations.length) {
    filterCount++
  }

  if (buildings.filters.buildingTypes.length) {
    filterCount++
  }

  if (buildings.filters.query.length) {
    filterCount++
  }

  return (
    <Permissions type={PermissionsType.BUILDING}>
      {(permissions) => {
        const { canCreate } = permissions as IPermissionsOnBuilding

        return (
          <div
            className={css.buildingManagementToolbar}
            style={{
              height: showFilters ? 183 : 70,
            }}
          >
            <Card.Header style={{ width: '100%' }}>
              <Flex align="center">
                <FilterButton
                  onClick={() => setShowFilters(!showFilters)}
                  filterCount={filterCount}
                />
                {filterCount ? (
                  <Flex align="baseline">
                    <span
                      style={{ marginLeft: 10, marginRight: 4 }}
                      className={buildings.status === 'loading' ? Classes.SKELETON : ''}
                    >
                      {prettyNumber({ value: buildings.count })} filtered{' '}
                      {pluralize(buildings.count, 'record')}
                    </span>
                    <IconButton
                      minimal
                      round
                      iconName="times"
                      tooltip="Clear filters"
                      onClick={buildings.resetFilters}
                    />
                  </Flex>
                ) : (
                  <span
                    style={{ marginLeft: 10, marginRight: 4 }}
                    className={buildings.status === 'loading' ? Classes.SKELETON : ''}
                  >
                    {prettyNumber({ value: buildings.count })}{' '}
                    {pluralize(buildings.count, 'record')}
                  </span>
                )}
              </Flex>
              {canCreate && (
                <div className="flex">
                  <div>
                    <Button
                      className="ml2"
                      text="New Building"
                      data-button="new_building"
                      iconName="plus"
                      onClick={() => {
                        setShowNew(true)
                        track.buildings({
                          eventName: Event.NEW_RECORD_DIALOG,
                          props: {
                            component: 'Building management',
                          },
                        })
                      }}
                    />
                  </div>
                  <Popover
                    position={Position.BOTTOM}
                    content={
                      <Menu>
                        <MenuItem
                          text="Import Buildings"
                          onClick={() => setImportingBuildings(true)}
                          icon={<Icon style={{ marginTop: 2, width: 22 }} name="file-import" />}
                        />
                        <MenuItem
                          text="Import Floors"
                          onClick={() => setImportingFloors(true)}
                          icon={<Icon style={{ marginTop: 2, width: 22 }} name="file-import" />}
                        />
                      </Menu>
                    }
                  >
                    <IconButton
                      round={false}
                      className="ml2"
                      iconName="ellipsis-h"
                      tooltip="More actions"
                      data-button="more_actions"
                    />
                  </Popover>
                </div>
              )}
              {importingBuildings && (
                <ImportFlow
                  title="Import Buildings"
                  importHook={importBuildings}
                  onClose={() => setImportingBuildings(false)}
                  onComplete={reload.buildings}
                >
                  <>
                    <p>
                      <LinkButton
                        onClick={() =>
                          api.building.template().then(dlTemplate('building_imports_template.csv'))
                        }
                        text="Download the .csv template"
                      />{' '}
                      for bulk operation building import
                    </p>
                    <ul>
                      <li>You can import multiple buildings, or a single building.</li>
                      <li>New data will merge with existing data.</li>
                    </ul>
                  </>
                </ImportFlow>
              )}
              {importingFloors && (
                <ImportFlow
                  title="Import Floors"
                  importHook={importFloors}
                  onClose={() => setImportingFloors(false)}
                  onComplete={reload.buildings}
                >
                  <>
                    <p>
                      <LinkButton
                        onClick={() =>
                          api.building.floor
                            .template()
                            .then(dlTemplate('floor_imports_template.csv'))
                        }
                        text="Download the .csv template"
                      />{' '}
                      for bulk operation floor import
                    </p>
                    <ul>
                      <li>You can import multiple floors, or a single floor.</li>
                      <li>New data will merge with existing data.</li>
                    </ul>
                  </>
                </ImportFlow>
              )}
            </Card.Header>
            <Collapse isOpen={showFilters} keepChildrenMounted>
              <Flex style={{ marginBottom: 10 }}>
                <FilterContainer>
                  <H6>Show Inactive Buildings</H6>
                  <Switch
                    checked={buildings.filters.includeOld}
                    style={{ margin: 0, width: 'min-content' }}
                    onChange={() => {
                      buildings.applyFilters({
                        ...buildings.filters,
                        includeOld: !buildings.filters.includeOld,
                      })

                      const payload: RootEvent = buildings.filters.includeOld
                        ? {
                            eventName: Event.CLEAR_FILTER,
                            props: {
                              filterName: 'Include old buildings',
                              filterValue: null,
                            },
                          }
                        : {
                            eventName: Event.FILTER,
                            props: {
                              filterName: 'Include old buildings',
                              filterValue: true,
                            },
                          }

                      track.buildings(payload)
                    }}
                  />
                </FilterContainer>
                <FilterContainer>
                  <MultiLocationSelect
                    mode={Mode.MULTI_REGION_CITY}
                    values={buildings.filters.cities}
                    includeOldBuildings={buildings.filters.includeOld}
                    onChange={(newSelection) => {
                      buildings.applyFilters({
                        ...buildings.filters,
                        cities: newSelection,
                      })

                      const payload: RootEvent = buildings.filters.cities.length
                        ? {
                            eventName: Event.CLEAR_FILTER,
                            props: {
                              filterName: 'Cities filter',
                              filterValue: null,
                            },
                          }
                        : {
                            eventName: Event.FILTER,
                            props: {
                              filterName: 'Cities filter',
                              filterValue: null,
                            },
                          }

                      track.buildings(payload)
                    }}
                  />
                </FilterContainer>
                <FilterContainer>
                  <MultiHRLocationSelect
                    values={buildings.filters.HRLocations}
                    onChange={(hrLocations) => {
                      buildings.applyFilters({
                        ...buildings.filters,
                        HRLocations: hrLocations,
                      })

                      const payload: RootEvent = buildings.filters.HRLocations.length
                        ? {
                            eventName: Event.CLEAR_FILTER,
                            props: {
                              filterName: 'HRLocations filter',
                              filterValue: null,
                            },
                          }
                        : {
                            eventName: Event.FILTER,
                            props: {
                              filterName: 'HRLocations filter',
                              filterValue: null,
                            },
                          }

                      track.buildings(payload)
                    }}
                  />
                </FilterContainer>
                <FilterContainer>
                  <BuildingTypeSelect
                    values={buildings.filters.buildingTypes}
                    onChange={(buildingType) => {
                      if (typeof buildingType === 'string') {
                        buildings.applyFilters({
                          ...buildings.filters,
                          buildingTypes: buildings.filters.buildingTypes.filter(
                            (i) => i.id !== buildingType
                          ),
                        })
                      }

                      if (typeof buildingType === 'object') {
                        buildings.applyFilters({
                          ...buildings.filters,
                          buildingTypes: buildings.filters.buildingTypes.find(
                            (i) => i.id === buildingType.id
                          )
                            ? buildings.filters.buildingTypes.filter(
                                (i) => i.id !== buildingType.id
                              )
                            : [...buildings.filters.buildingTypes, buildingType],
                        })
                      }

                      const payload: RootEvent =
                        typeof buildingType !== 'string'
                          ? {
                              eventName: buildings.filters.buildingTypes.find(
                                (i) => i.id === buildingType.id
                              )
                                ? Event.CLEAR_FILTER
                                : Event.FILTER,
                              props: {
                                filterName: 'Building type filter',
                                filterValue: null,
                              },
                            }
                          : {
                              eventName: Event.CLEAR_FILTER,
                              props: {
                                filterName: 'HRLocations filter',
                                filterValue: null,
                              },
                            }

                      track.buildings(payload)
                    }}
                  />
                </FilterContainer>
              </Flex>
            </Collapse>
            <NewBuildingDialog
              isOpen={showNew}
              closeDialog={() => setShowNew(false)}
              onSuccess={(building: any) => {
                setShowNew(false)
                reload.buildings()

                navigate?.(`/buildingManager?buildingId=${building.id}`)
              }}
            />
          </div>
        )
      }}
    </Permissions>
  )
}

interface NewBuildingDialogProps {
  isOpen: boolean | undefined
  closeDialog: () => void
  onSuccess: (building: any) => void
}

function NewBuildingDialog({ isOpen, closeDialog, onSuccess }: NewBuildingDialogProps) {
  const [dirty, setDirty] = useState(false)
  return (
    <Dialog
      style={{ width: 500 }}
      canEscapeKeyClose={false}
      canOutsideClickClose={!dirty}
      title="New Building"
      usePortal
      isOpen={isOpen}
      isCloseButtonShown={false}
      toolbar={
        !dirty ? (
          <div className="dialog-buttons">
            <IconButton minimal tooltip="Close" iconName="times" onClick={closeDialog} />
          </div>
        ) : (
          <></>
        )
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          <NewBuildingForm
            dirty={dirty}
            setDirty={setDirty}
            onCancel={() => {
              setDirty(false)
              closeDialog()
            }}
            onSuccess={(building) => {
              setDirty(false)
              onSuccess(building)
              Toaster.success({ message: 'Building created' })
            }}
          />
        </div>
      }
    />
  )
}
